import {
  enableProdMode,
  provideZoneChangeDetection,
  ErrorHandler,
  provideAppInitializer,
  inject,
  importProvidersFrom,
} from "@angular/core";

import { environment } from "./environments/environment";
import * as Sentry from "@sentry/browser";
import { EventService } from "./app/shared/event/event.service";
import { ToastModule } from "primeng/toast";
import { MessageService } from "primeng/api";
import { GoogleAnalyticsEventsService } from "./app/shared/google/google-analytics-events.service";
import { providePrimeNG } from "primeng/config";
import { QuotissPreset } from "./app/theme/quotiss-preset";
import { SentryErrorHandler } from "./app/shared/error-handler/sentry-error-handler";
import { sentryEnvironment } from "./app/shared/error-handler/sentry-environment";
import { sentryRelease } from "./app/shared/error-handler/sentry-release";
import { sentryBeforeSend } from "./app/shared/error-handler/sentry-before-send";
import {
  isGoogleMapsModuleLoadFailure,
  isPdfJsAbort,
  isRecoveredChunkLoadError,
} from "./app/shared/error-handler/sentry-ignore";
import { setSentryUser } from "./app/shared/error-handler/sentry-user";
import { UserService } from "./app/user/user.service";
import { NgxPermissionsService, NgxPermissionsModule } from "ngx-permissions";
import { noop } from "./app/shared/noop.function";
import { BrowserModule, bootstrapApplication } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { SecurityModule } from "./app/security/security.module";
import { UserModule } from "./app/user/user.module";
import { UiModule } from "./app/ui/ui.module";
import { QuoteModule } from "./app/quote/quote.module";
import { AppRoutingModule } from "./app/app-routing.module";
import { AppComponent } from "./app/app.component";

if (environment.production) {
  enableProdMode();
}

if (environment.sentryEnabled) {
  Sentry.init({
    dsn: "https://18a84b2361c6afde74e039e890bbda60@o4511582121361408.ingest.de.sentry.io/4511582147772496",
    ignoreErrors: [
      /Permission denied/,
      "NS_ERROR_NOT_INITIALIZED",
      "groove", // Loading Groove widget
      "freed script",
      'Event: {"isTrusted":true}', // Loading Google Maps in China
    ],
    denyUrls: [/groove/],
    release: sentryRelease(environment.version),
    environment: sentryEnvironment(window.location.hostname),
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.02,
    // Same-origin API only; the backend continues a trace the browser sampled.
    tracePropagationTargets: [/^\/api\//],
    beforeSend: (event, hint) =>
      isPdfJsAbort(event) ||
      isGoogleMapsModuleLoadFailure(event) ||
      isRecoveredChunkLoadError(event)
        ? null
        : sentryBeforeSend(event, hint),
  });
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      ToastModule,
      BrowserModule,
      BrowserAnimationsModule,
      SecurityModule,
      UserModule,
      UiModule,
      NgxPermissionsModule.forRoot(),
      QuoteModule,
      AppRoutingModule,
    ),
    EventService,
    ToastModule,
    MessageService,
    GoogleAnalyticsEventsService,
    providePrimeNG({
      theme: {
        preset: QuotissPreset,
        options: {
          darkModeSelector: "none",
        },
      },
      license:
        "eyJpZCI6IjJmMmNlZDA2LWVkNDUtNGZiYi1hNDBkLTEyYTM1ZjdjZmU2YyIsInByb2R1Y3QiOiJwcmltZXVpIiwidGllciI6ImNvbW11bml0eSIsInR5cGUiOiJkZXYiLCJpYXQiOjE3ODQwMTAyMTcsImV4cCI6MTgxNTU0NjIxN30.SqAZ6dpCkeq2iMFH_vkJQT1Zl3s72vjvhjQCApa_OkveZ3up0-7AMt857iE9PZPuXOBmAg9OC14EO3fJaPHOBg",
    }),
    {
      provide: ErrorHandler,
      useClass: SentryErrorHandler,
    },
    provideAppInitializer(() => {
      const userService = inject(UserService);
      const permissionsService = inject(NgxPermissionsService);
      return userService.getUser().then(
        (user) => {
          if (user) {
            permissionsService.loadPermissions(user.getRoles());
            setSentryUser(user);
          }
        },

        () => {
          noop();
        },
      );
    }),
  ],
}).catch((e) => {
  Sentry.captureException(e);
  console.error("Bootstrap exception: ", e);
});
